import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { SITE } from "@/constants/site";
import { rememberLang, useLang } from "@/i18n/LangContext";

const LangSwitcher = ({ onSwitch }) => {
  const { lang, otherLang, switchPath } = useLang();
  const navigate = useNavigate();

  const go = () => {
    rememberLang(otherLang);
    if (onSwitch) onSwitch();
    navigate(switchPath);
  };

  const btn = (code) => {
    const active = lang === code;
    return (
      <button
        key={code}
        type="button"
        data-testid={`lang-switch-${code}`}
        aria-pressed={active}
        aria-label={code === "fr" ? "Voir le site en français" : "View the site in English"}
        onClick={active ? undefined : go}
        className={`rounded-full px-2.5 py-1 text-xs font-bold tracking-wide transition-colors duration-300 ${
          active ? "bg-ink text-cream" : "text-plum/60 hover:text-ink"
        }`}
      >
        {code.toUpperCase()}
      </button>
    );
  };

  return (
    <div
      className="flex items-center gap-0.5 rounded-full border border-ink/10 bg-white/60 p-1"
      data-testid="lang-switcher"
      role="group"
      aria-label="Langue / Language"
    >
      {btn("fr")}
      {btn("en")}
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t, l } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl border-b border-ink/5 shadow-[0_8px_30px_-15px_rgba(17,14,13,0.15)]"
          : "bg-cream/40 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-10 h-[76px]">
        <Link
          to={l("/")}
          data-testid="header-logo"
          className="flex items-center gap-2.5 font-display text-lg font-bold tracking-tight"
          aria-label={t.nav.homeAria}
        >
          <img
            src="/assets/logo-consultante-strategie-de-marque-pme.png"
            alt={t.logoAlt}
            className="h-[52px] w-[52px] rounded-xl object-cover shadow-[0_8px_20px_-8px_rgba(17,14,13,0.3)]"
          />
          <span className="hidden sm:block uppercase">
            Magali Clouzet
            <span className="-mt-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-terracotta-deep">
              {t.nav.tagline}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label={t.nav.mainAria}>
          {t.nav.links.map((link) => (
            <NavLink
              key={link.path}
              to={l(link.path)}
              data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  isActive ? "bg-ink text-cream" : "text-plum/80 hover:bg-ink/5 hover:text-ink"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitcher />
          <a
            href={SITE.calendly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-rdv"
            className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-terracotta-dark"
          >
            {t.nav.bookCall}
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-ink/10 bg-white/60"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-ink/5 bg-cream/95 backdrop-blur-xl"
            aria-label={t.nav.mobileAria}
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {t.nav.links.map((link) => (
                <NavLink
                  key={link.path}
                  to={l(link.path)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={({ isActive }) =>
                    `rounded-2xl px-5 py-3.5 text-base font-medium transition-colors duration-200 ${
                      isActive ? "bg-ink text-cream" : "text-plum hover:bg-ink/5"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <a
                href={SITE.calendly}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-cta-rdv"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-5 py-4 text-sm font-semibold text-ink"
              >
                {t.nav.bookCall}
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
